import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useSEO } from '../../../hooks/useSEO';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useLocation } from 'react-router-dom';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const PLACFPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();
  const materialData = TEXT.materials?.['pla-cf'];

  useSEO({
    title: 'PLA-CF',
    description: isEnglish
      ? 'High-rigidity PLA with carbon fiber. Lightweight and strong, excellent dimensional stability. Ideal for drone parts and industrial jigs. Hardened steel nozzle required.'
      : 'カーボン繊維による高剛性PLA。軽量で高強度、優れた寸法安定性。ドローン部品や工業用治具に最適。硬化スチール製ノズル必須。',
    keywords: '3Dプリンター,PLA-CF,カーボン繊維,フィラメント,高剛性,軽量',
    canonicalPath: isEnglish ? '/en/filament/pla-cf' : '/filament/pla-cf'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' },
    { label: 'PLA-CF', path: '/en/filament/pla-cf' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' },
    { label: 'PLA-CF', path: '/filament/pla-cf' }
  ];

  const filamentId = 'pla-cf';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          {/* メインコンテンツ */}
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                PLA-CF
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="pla-cf" />

            {/* 特徴 */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  {materialData?.features.slice(0, 2).map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-400 mt-1"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{materialData?.features[2]}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-intermediate px-3 py-1 rounded text-sm font-semibold">{TEXT.intermediate}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{isEnglish ? 'Fiber Content:' : '繊維含有率：'}</span>
                      <span className="text-white">15-20%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.3 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.2-0.3%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">60-70°C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">200-230°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'High temp for fiber dispersion' : '繊維分散のため高温'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">60-80°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Prevent warping' : '反り防止'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">50-80%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Balance with layer adhesion' : '層間接着とのバランス'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">30-50mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Slow for fiber dispersion' : '繊維分散のため低速'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4-0.6mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Hardened steel required' : '硬化スチール製必須'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Nozzle wear prevention required, ventilation recommended' : 'ノズル摩耗対策必須、換気推奨'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="PLA-CF"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {materialData?.derivatives}
              </p>
              <div className="space-y-4 text-metal-light">
                <div className="border-l-2 border-accent pl-4">
                  <span className="text-white font-semibold">PLA-CF Pro</span>
                  <p className="mt-1">{isEnglish ? 'High carbon fiber content. Achieves higher rigidity and strength.' : '高含有率カーボン繊維。より高い剛性と強度を実現。'}</p>
                </div>
                <div className="border-l-2 border-accent pl-4">
                  <span className="text-white font-semibold">PLA-CF Conductive</span>
                  <p className="mt-1">{isEnglish ? 'Conductivity-focused formulation. Used for electronic enclosures and static protection.' : '導電性重視の配合。電子部品の筐体や静電気対策に使用。'}</p>
                </div>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PLACFPage;
